import { toast } from "react-toastify";

import { useRouter } from "components/hooks";
import { get } from "utils/request";

import { getMagnetDetailURL } from "apis/magnets";
import { getBrandPublicProfile } from "apis/brand";
import { useEffect, useState } from "react";

import Loading from "components/atoms/Loading";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { CurveArrow, MagnetiqLogo } from "components/atoms/Icon";
import BrandSocialCardNew from "components/molecules/brand/BrandSocialCardNew";
import ClaimMagnetCTA from "components/molecules/magnet/ClaimMagnetCTA";
import moment from "moment";
import MagnetBenefitCard from "components/molecules/magnet/MagnetBenefitCard";
import Footer from "components/pageComponents/Footer/Footer";
import clsx from "clsx";
import { ROUTES, SPECIAL_RENDRING_BRAND_IDS, TOAST_CONFIG } from "../../constants";

function ClaimMagnet() {
  const router = useRouter();

  const { magnetId }: { magnetId: string; eventId: string } = router.query;
  const [isCustomizedUIBrand, setIsCustomizedUIBrand] = useState(false);

  function handleGetMagnetFailure(errorMessage: string, error: any) {
    toast.error(errorMessage, TOAST_CONFIG);
    if (error.response.status === 404) {
      router.go(`/${ROUTES.NOT_FOUND}`);
      return;
    }
    throw new Error(errorMessage);
  }

  const getMagnetDetails = () =>
    get(getMagnetDetailURL(magnetId), {
      processError: handleGetMagnetFailure,
    });

  const {
    isLoading: isMagnetDetailsLoading,
    error: magnetDetailsError,
    data: magnetDetails,
  } = useQuery({
    queryKey: [magnetId],
    queryFn: getMagnetDetails,
  });

  useEffect(() => {
    setIsCustomizedUIBrand(SPECIAL_RENDRING_BRAND_IDS.includes(magnetDetails?.brand_id));
  }, [magnetDetails?.brand_id]);

  const getBrandDetails = () =>
    get(getBrandPublicProfile(magnetDetails.brand_id), {
      processError: handleGetMagnetFailure,
    });

  const { isLoading: isBrandLinksLoading, data: brandDetails } = useQuery({
    queryKey: [magnetDetails?.brand_id],
    queryFn: getBrandDetails,
    enabled: !!magnetDetails?.brand_id,
  });

  const onLoginAfterSubmit = () => {
    if (magnetDetails) {
      router.go(`/brand/${magnetDetails.brand_id}/magnet/${magnetDetails.id}`);
    }
  };

  if (magnetDetailsError && !magnetDetails) {
    return null;
  }
  if (isMagnetDetailsLoading || isBrandLinksLoading) return <Loading />;

  return (
    <div
      className={clsx(
        "fixed left-0 top-0 z-50 mx-auto flex h-full w-full flex-col overflow-auto px-0",
        isCustomizedUIBrand ? "bg-[#e6e7e8] text-primary" : "bg-primary text-white",
      )}
    >
      {/* Navbar Loaded */}
      <nav
        className={clsx(
          "flex items-center justify-between px-4 py-4 md:px-28",
          isCustomizedUIBrand ? "bg-[#2a5070] [&>*]:invisible" : "bg-black",
        )}
      >
        <Link to="/">
          <MagnetiqLogo />
        </Link>
      </nav>
      {/* Main */}
      <main className="grid flex-1 grid-cols-1 gap-8 px-5 md:mt-12 md:px-28 lg:grid-cols-2 ">
        <article className=" order-2 overflow-y-auto lg:order-1">
          <BrandSocialCardNew
            name={brandDetails.name}
            profile_image_url={brandDetails.profile_image_url}
            website_link={brandDetails.website_link}
            instagram_link={brandDetails.instagram_link}
            twitter_link={brandDetails.twitter_link}
            other_link={brandDetails.other_link}
          />

          <section className="pt-12">
            <div className=" my-2 flex items-center space-x-2 font-satoshi text-lg font-medium opacity-60">
              <CurveArrow />
              <p>
                {"Launched on "}
                {moment(magnetDetails.created_at).format("MMMM D, YYYY")}
              </p>
              {/* <EllipsisHorizontalIcon className="text-red h-8 w-8 cursor-pointer" onClick={workInProgressAlert} /> */}
            </div>
            <h1 className="text-xl font-extrabold md:text-3xl 2xl:text-5xl">{magnetDetails.name}</h1>
            <p className="mt-3 text-lg font-light leading-8 opacity-70">{magnetDetails.magnetized_because}</p>
          </section>
          <hr className="my-10 w-[80%]" />
          <section className="max-w-[90%]">
            <h2 className="mb-6 text-2xl font-bold">Program Details</h2>
            {magnetDetails.benefits.map((benefit: any) => (
              <MagnetBenefitCard
                key={`mag_feature_mobile${benefit.id}`}
                title={benefit.title}
                image_url={benefit?.image_url}
                description={benefit?.description}
              />
            ))}
          </section>
        </article>
        <div className="order-1  lg:order-2 ">
          <ClaimMagnetCTA
            isCustomizedUIBrand={isCustomizedUIBrand}
            isCustomClaimForm={magnetDetails.claim_form_data_fields && magnetDetails.claim_form_data_fields.length > 0}
            formFields={magnetDetails.claim_form_data_fields}
            magnetName={magnetDetails.name}
            magnetCreatedAt={magnetDetails.created_at}
            magnet_image_url={magnetDetails.image_url}
            claimText={magnetDetails.claim_text}
            onLoginAfterSubmit={onLoginAfterSubmit}
          />
        </div>
      </main>
      <div className={`${isCustomizedUIBrand ? "bg-black" : "bg-primary"} px-4 py-6 md:px-28`}>
        <Footer />
      </div>
    </div>
  );
}
export default ClaimMagnet;
